import * as React from 'react';
import classnames from 'classnames';

import { IPropertyBarProps } from '../type';
import { imageStorage } from '../../../../service/image_storage';
import {
  applyImageFilter,
  IMAGE_FILTER_OPTIONS,
  ImageFilterName,
} from '../../../../utils/imageFilter';

interface SelectedImageInfo {
  elementId: string;
  contentId: string;
  originalContentId: string;
  filterName: ImageFilterName;
}

interface ImageFilterApi {
  get_selected_image_info?: () => unknown;
  update_selected_image_filter?: (filterName: string, contentId: string) => void;
}

interface RotationApi {
  get_selection_rotation?: () => number;
  update_selection_rotation?: (rotation: number) => void;
}

const formatRotation = (value: number): string =>
  Number.isFinite(value) ? Number(value.toFixed(2)).toString() : '0';

const ImageProperties = ({ app, onStyleChange, onRequestRender }: IPropertyBarProps) => {
  const api = app as unknown as ImageFilterApi;
  const rotationApi = app as unknown as RotationApi;
  const selectionRotation = rotationApi.get_selection_rotation?.() ?? 0;
  const rawInfo = api.get_selected_image_info?.();
  const info = React.useMemo<SelectedImageInfo | null>(() => {
    if (!rawInfo) {
      return null;
    }

    const read = (key: string): unknown =>
      rawInfo instanceof Map ? rawInfo.get(key) : (rawInfo as Record<string, unknown>)[key];

    const elementId = read('elementId');
    const contentId = read('contentId');
    const originalContentId = read('originalContentId');
    const filterName = read('filterName');
    if (
      typeof elementId !== 'string' ||
      typeof contentId !== 'string' ||
      typeof originalContentId !== 'string'
    ) {
      return null;
    }

    const resolvedFilter =
      typeof filterName === 'string' &&
      IMAGE_FILTER_OPTIONS.some(option => option.value === filterName)
        ? (filterName as ImageFilterName)
        : 'none';

    return {
      elementId,
      contentId,
      originalContentId,
      filterName: resolvedFilter,
    };
  }, [rawInfo]);
  const cacheRef = React.useRef<Map<string, string>>(new Map());
  const [isApplying, setIsApplying] = React.useState(false);
  const [rotation, setRotation] = React.useState(formatRotation(selectionRotation));

  React.useEffect(() => {
    setRotation(formatRotation(selectionRotation));
  }, [selectionRotation]);

  const activeFilter: ImageFilterName =
    info?.filterName && IMAGE_FILTER_OPTIONS.some(option => option.value === info.filterName)
      ? info.filterName
      : 'none';

  const applyFilter = React.useCallback(
    async (nextFilter: ImageFilterName) => {
      if (!info || !api.update_selected_image_filter) {
        return;
      }
      if (nextFilter === activeFilter) {
        return;
      }

      setIsApplying(true);
      try {
        let nextContentId = info.originalContentId || info.contentId;

        if (nextFilter !== 'none') {
          const cacheKey = `${nextContentId}:${nextFilter}`;
          const cached = cacheRef.current.get(cacheKey);
          if (cached) {
            nextContentId = cached;
          } else {
            const original = await imageStorage.getImage(nextContentId);
            if (!original) {
              return;
            }

            const filteredBlob = await applyImageFilter(original.blob, nextFilter);
            nextContentId = await imageStorage.saveImage(filteredBlob);
            cacheRef.current.set(cacheKey, nextContentId);
          }
        }

        api.update_selected_image_filter(nextFilter, nextContentId);
        onRequestRender?.();
        onStyleChange?.();
      } finally {
        setIsApplying(false);
      }
    },
    [activeFilter, api, info, onRequestRender, onStyleChange],
  );

  if (!info) {
    return null;
  }

  const commitRotation = () => {
    const next = Number.parseFloat(rotation);
    rotationApi.update_selection_rotation?.(Number.isFinite(next) ? next : selectionRotation);
    onStyleChange?.();
  };

  return (
    <>
      <div className="prop-items-group">
        <div className="prop-section-title">Transform</div>
        <div className={classnames('prop-item', 'prop-rotate')}>
          <label htmlFor="image-rotate">Rotate</label>
          <input
            id="image-rotate"
            type="number"
            step={1}
            value={rotation}
            onChange={e => setRotation(e.target.value)}
            onBlur={commitRotation}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                commitRotation();
              }
            }}
          />
        </div>
      </div>
      <div className="prop-items-group">
        <div className="prop-section-title">Image</div>
        <div className={classnames('prop-item', 'prop-image-filter')}>
          <label htmlFor="image-filter-select">Filter</label>
          <select
            id="image-filter-select"
            value={activeFilter}
            disabled={isApplying}
            onChange={event => {
              void applyFilter(event.target.value as ImageFilterName);
            }}
          >
            {IMAGE_FILTER_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </>
  );
};

export default ImageProperties;
